import { createHmac } from 'crypto';
import { NextResponse } from 'next/server';
import { v4 as uuidv4 } from 'uuid';

import { Env } from '@/lib/Env';
import { PricingCategoryEnumType } from '@/types/enumTypes';

/**
 * Returns a Response object with a JSON body
 */
export function jsonResponse(status: number, data: any, init?: ResponseInit) {
  return new NextResponse(JSON.stringify(data), {
    ...init,
    status,
    headers: {
      ...init?.headers,
      'Content-Type': 'application/json',
    },
  });
}

export function getHmacSignature(
  key: string,
  requestMethod: string,
  requestUrl: string
) {
  // 1. Get the Request HTTP Method type
  const requestHttpMethod = requestMethod;

  // 2. Calculate UNIX time
  // get and set a timestamp in seconds (Unix epoch)
  const requestTimeStamp = Math.floor(Date.now() / 1000);

  // 3. Create the random nonce for each request
  const nonce = uuidv4();

  const signatureRawData = `${requestHttpMethod}${`/${requestUrl}`}${requestTimeStamp}${nonce}`;

  const secretBuffer = Buffer.from(key, 'ascii');
  const valueBuffer = Buffer.from(signatureRawData, 'ascii');
  const requestTimeStampBuffer = Buffer.from(
    requestTimeStamp.toString(),
    'ascii'
  );
  const nonceBuffer = Buffer.from(nonce, 'ascii');
  const hmac = createHmac('sha256', secretBuffer);
  hmac.update(valueBuffer);
  const hmacDigest = hmac.digest('base64');

  return `${hmacDigest}:${nonceBuffer.toString(
    'base64'
  )}:${requestTimeStampBuffer.toString('base64')}`;
}

export function generatePreAuthorizationToken(
  requestMethod: string,
  requestUrl: string
) {
  const preAuthSecretKey = Env.PreAuthSecretKey || '';

  const preAuthToken = `${getHmacSignature(
    preAuthSecretKey,
    requestMethod,
    requestUrl
  )}`;

  return preAuthToken;
}

export function globalAssignRefName(pathName: string, isIRSReferral: boolean) {
  let refName = '';
  switch (pathName) {
    case '/tax-extension':
      refName = 'extension';
      break;
    case '/tax-extension/efile-irs-form-7004-business-tax-extension-online':
      refName = isIRSReferral ? 'form7004irs' : 'form7004';
      break;
    case '/tax-extension/efile-irs-form-4868-personal-tax-extension-online':
      refName = 'form4868';
      break;
    case '/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online':
      refName = 'form8868';
      break;
    case '/excise-tax':
      refName = 'excise';
      break;
    case '/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online':
      refName = 'form2290';
      break;
    case '/excise-tax/efile-irs-form-8849-schedule-6-online':
      refName = 'form8849';
      break;
    case '/excise-tax/efile-irs-form-2290-vin-correction-online':
      refName = 'form2290VIN';
      break;
    case '/excise-tax/efile-irs-form-2290-weight-increased-amendment':
      refName = 'form2290weightincreased';
      break;
    case '/excise-tax/efile-irs-form-2290-mileage-exceeded-amendment':
      refName = 'form2290mileageexceeded';
      break;
    case '/pricing':
      refName = 'pricing';
      break;
    case '/tax-professionals':
      refName = 'taxprofessional';
      break;
    case '/mobile-apps':
      refName = 'apps';
      break;
    case '/tools/form-2290-tax-calculator':
      refName = '2290taxcalculator';
      break;
    case '/contact-us':
      refName = 'contact';
      break;
    case '/about-us':
      refName = 'about';
      break;
    case '/faqs':
      refName = 'faqs';
      break;
    case '/features':
      refName = 'features';
      break;
    case '/press-releases':
      refName = 'pressreleases';
      break;
    case '/resources/excise-tax/form-2290-instructions':
      refName = '2290instructions';
      break;
    case '/resources/excise-tax/form-2290-due-dates':
      refName = '2290duedates';
      break;
    case '/resources/excise-tax/form-2290-taxable-suspended-vehicles':
      refName = '2290taxable';
      break;
    case '/resources/excise-tax/form-2290-irs-payment-options':
      refName = '2290payment';
      break;
    case '/resources/excise-tax/form-2290-amendments':
      refName = '2290amendments';
      break;
    case '/resources/excise-tax/form-2290-credit-vehicles':
      refName = '2290credit';
      break;
    case '/resources/excise-tax/form-2290-schedule-1':
      refName = '2290schedule1';
      break;
    case '/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories':
      refName = '2290weight';
      break;
    case '/resources/tax-extension/form-7004-instructions':
      refName = '7004instructions';
      break;
    case '/resources/tax-extension/form-4868-instructions':
      refName = '4868instructions';
      break;
    case '/resources/tax-extension/form-8868-instructions':
      refName = '8868instructions';
      break;
    case '/employment-tax/e-file-irs-form-941':
      refName = 'form941';
      break;
    case '/employment-tax/e-file-irs-form-940':
      refName = 'form940';
      break;
    case '/employment-tax/e-file-irs-form-94x-pin-request':
      refName = 'form94xpin';
      break;
    case '/tax-exempt/e-file-irs-form-990-ez':
      refName = 'form990ez';
      break;
    case '/tax-exempt/e-file-irs-form-990':
      refName = 'form990';
      break;
    case '/tax-exempt/e-file-irs-form-990-pf':
      refName = 'form990pf';
      break;
    case '/information-returns/efile-irs-form-1099-nec':
      refName = 'form1099nec';
      break;
    case '/information-returns/efile-irs-form-1099-misc':
      refName = 'form1099misc';
      break;
    case '/information-returns/efile-irs-form-1099-int':
      refName = 'form1099int';
      break;
    case '/paystub-generator':
      refName = 'paystubgenerator';
      break;
    case '/tools/form-7004-due-date-calculator':
      refName = 'form7004duedatecalculator';
      break;
    case '/privacy-policy':
      refName = 'privacypolicy';
      break;
    case '/terms-of-use':
      refName = 'termsofuse';
      break;
    case '/resources/employment-tax/form-941-instructions-2023':
      refName = '941instructions2023';
      break;
    case '/resources/employment-tax/form-941-instructions':
      refName = '941instruction';
      break;
    case '/resources/tax-exempt/form-990-schedule-o':
      refName = '990scho';
      break;
    case '/resources/tax-exempt/form-990-schedule-n':
      refName = '990schn';
      break;
    case '/resources/tax-exempt/form-990-schedule-l':
      refName = '990schl';
      break;
    case '/resources/tax-exempt/form-990-schedule-g':
      refName = '990schg';
      break;
    case '/resources/tax-exempt/form-990-schedule-e':
      refName = '990sche';
      break;
    case '/resources/tax-exempt/form-990-schedule-c':
      refName = '990schc';
      break;
    case '/resources/tax-exempt/form-990-schedule-b':
      refName = '990schb';
      break;
    case '/resources/tax-exempt/form-990-schedule-a':
      refName = '990scha';
      break;
    case '/resources/tax-exempt/form-990-n-instructions':
      refName = '990ninstructions';
      break;
    case '/resources/information-returns/form-w-2-instructions':
      refName = 'w2instruction';
      break;
    case '/resources/information-returns/form-1099-misc-instructions':
      refName = '1099miscinstriction';
      break;
    case '/resources/information-returns/form-1099-nec-instructions':
      refName = '1099necinstruction';
      break;
    case '/resources/excise-tax/hvut-form-2290-penalties':
      refName = 'huvtpenalties';
      break;
    case '/resources/eftps':
      refName = 'eftps';
      break;
    case '/resources/tax-extension/form-8868-due-dates':
      refName = '8868duedate';
      break;
    case '/resources/tax-extension/form-4868-due-dates':
      refName = '4868duedate';
      break;
    case '/resources/tax-extension/form-7004-and-4868-comparison':
      refName = '7004and4868comparison';
      break;
    case '/resources/tax-extension/form-7004-form-codes':
      refName = '7004formcodes';
      break;
    case '/resources/tax-extension/form-7004-for-form-1120':
      refName = 'form1120';
      break;
    case '/resources/tax-extension/form-7004-for-form-1065':
      refName = 'form1065';
      break;
    case '/resources/tax-extension/form-7004-for-form-1042':
      refName = 'form1042';
      break;
    case '/resources/tax-extension/form-7004-due-dates':
      refName = '7004duedate';
      break;
    case '/free-filing':
      refName = 'freefiling';
      break;
    case '/features/click2file':
      refName = 'click2file';
      break;
    case '/information-returns/efile-irs-form-1099-corrections':
      refName = 'form1099correction';
      break;
    case '/information-returns/efile-irs-form-1099-r':
      refName = 'form1099r';
      break;
    case '/information-returns/efile-irs-form-1099-g':
      refName = 'form1099g';
      break;
    case '/information-returns/efile-irs-form-1099-k':
      refName = 'form1099k';
      break;
    case '/information-returns/efile-irs-form-1099-b':
      refName = 'form1099b';
      break;
    case '/information-returns/efile-irs-form-1099-div':
      refName = 'form1099div';
      break;
    case '/information-returns/efile-irs-form-w2-c':
      refName = 'formw2c';
      break;
    case '/information-returns/efile-irs-form-w2':
      refName = 'formw2';
      break;
    case '/information-returns':
      refName = 'information';
      break;
    case '/tax-exempt':
      refName = 'exempt';
      break;
    case '/employment-tax':
      refName = 'employment';
      break;
    default:
      refName = 'menu';
  }
  return refName;
}

export type PricingCategory = {
  PricingCategoryId: number;
  PricingCategoryType: PricingCategoryEnumType;
  FromCount: number;
  ToCount: number;
  Amount: number;
  AvaliableCredits: number;
  displayorder: number;
};

function Data(
  PricingCategoryId: number,
  PricingCategoryType: PricingCategoryEnumType,
  FromCount: number,
  ToCount: number,
  Amount: number,
  AvaliableCredits: number,
  displayorder: number
): PricingCategory {
  return {
    PricingCategoryId,
    PricingCategoryType,
    FromCount,
    ToCount,
    Amount,
    AvaliableCredits,
    displayorder,
  };
}
export const informationPricingCategoryDetails = [
  Data(1, PricingCategoryEnumType.FederalFiling, 1, 25, 2.49, 0, 1),
  Data(2, PricingCategoryEnumType.FederalFiling, 26, 50, 1.99, 0, 2),
  Data(3, PricingCategoryEnumType.FederalFiling, 51, 100, 1.59, 0, 3),
  Data(4, PricingCategoryEnumType.FederalFiling, 101, 250, 1.29, 0, 4),
  Data(5, PricingCategoryEnumType.FederalFiling, 251, 500, 1.09, 0, 5),
  Data(6, PricingCategoryEnumType.FederalFiling, 501, 1000, 0.79, 0, 6),
  Data(7, PricingCategoryEnumType.FederalFiling, 1001, -1, 0.59, 0, 7),
  Data(8, PricingCategoryEnumType.StateFiling, 1, -1, 0.99, 0, 8),
  Data(9, PricingCategoryEnumType.PostalMailing, 1, -1, 1.75, 0, 9),
  Data(10, PricingCategoryEnumType.OnlineFiling, 1, -1, 0.5, 0, 10),
];

export function getUsDollarFormattedAmount(amount: number) {
  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return USDollar.format(amount);
}

export function ConvertAmountFormat(Amount: number) {
  if (Amount !== undefined) {
    return `$${Amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
  }

  return `$0.00`;
}

export function getLastDateOfTheMonth(
  year: number,
  month: number,
  day: number
) {
  // in javascript month is 0 indexed, hence for month of January, month - 1 (i.e., 0)
  const lastDate = new Date(year, month - 1, day);
  lastDate.setHours(23, 59, 59);
  return lastDate;
}
export const FormCardList = [
  {
    displayOrder: 1,
    FormCategoryId: 1,
    title: 'Extension Forms',
    HighlightText1: 'Extension',
    HighlightText2: 'Forms',
    description: '7004, 4868, 8868, 8809',
    image: '/assets/images/ext-img.webp',
    icon: {
      webp: '/assets/images/extension-icon.webp',
      png: '/assets/images/extension-icon.png',
      alt: 'Extension Forms',
    },
    bgColor: '#e6eaf7',
    heading: {
      text: 'Easily File Your ',
      highlight: 'Tax Extensions',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-extension/`,
      suffix: ' with TaxZerone',
    },
    intro: `Need more time to file your taxes? TaxZerone makes it simple, secure, and affordable to file your <span style="font-weight: 600;">tax extension forms</span> online in minutes.`,
    whychoosetitle: 'Why Choose TaxZerone?',
    whychoosedescription:
      'We go beyond just e-filing—our advanced features make tax filing easier and faster:',
    supportedFormsTitle: 'We Support:',
    supportedForms: [
      {
        name: 'Form 7004',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-extension/efile-irs-form-7004-business-tax-extension-online/`,
        desc: 'Business tax extension for corporations, partnerships, and trusts (Forms 1120, 1065, 1041, and more)',
      },
      {
        name: 'Form 4868',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-extension/efile-irs-form-4868-personal-tax-extension-online/`,
        desc: 'Personal tax extension for individuals filing Form 1040.',
      },
      {
        name: 'Form 8868',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/`,
        desc: 'Nonprofit Tax Extension (Form 990 series)',
      },
      {
        name: 'Form 8809',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-extension/efile-irs-form-8809-online/`,
        desc: 'Extension to file information returns (such as Forms 1099, W-2, and more)',
      },
    ],
    features: [
      {
        title: 'Bulk Upload',
        desc: 'Effortlessly file multiple extensions at once.',
      },
      {
        title: 'Click2File',
        desc: 'Quickly copy data from your previous filings to save time.',
      },
      {
        title: 'Instant IRS Status Updates',
        desc: 'Get real-time notifications on your filing status.',
      },
    ],
  },
  {
    displayOrder: 2,
    FormCategoryId: 3,
    title: 'Exempt Org. Forms',
    HighlightText1: 'Exempt Org.',
    HighlightText2: 'Forms',
    description: '990-N, 990-EZ, 990, 990-PF, 990-T, 5227, CA 199',
    image: '/assets/images/expt-img.webp',
    icon: {
      webp: '/assets/images/nonprofit-icon.webp',
      png: '/assets/images/nonprofit-icon.png',
      alt: 'Exempt Organizations Forms',
    },
    bgColor: '#f6e6ea',
    heading: {
      text: 'Effortless E-Filing for ',
      highlight: 'Exempt Organization',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/`,
      suffix: ' Tax Forms',
    },
    intro: `TaxZerone ensures a <span style="font-weight: 600;">fast, accurate, and IRS-compliant </span>filing experience for nonprofits of all sizes. Whether you're a small organization or a large foundation, we support a full range of IRS and state tax forms to help you stay compliant.`,
    whychoosetitle: 'Schedules & Supporting Forms',
    whychoosedescription:
      'We go beyond just e-filing—our advanced features make tax filing easier and faster:',
    supportedFormsTitle: 'We Support:',
    supportedForms: [
      {
        name: 'Form 990-N (e-Postcard)',
        url: `${process.env.NEXT_PUBLIC_990N_SITE_URL}`,
        desc: 'For small tax-exempt organizations',
      },
      {
        name: 'Form 990-EZ',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/e-file-irs-form-990-ez/`,
        desc: 'For mid-sized tax-exempt organizations',
      },
      {
        name: 'Form 990',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/e-file-irs-form-990/`,
        desc: 'For larger tax-exempt organizations',
      },
      {
        name: 'Form 990-PF',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/e-file-irs-form-990-pf/`,
        desc: 'For private foundations',
      },
      {
        name: 'Form 990-T',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/e-file-irs-form-990-t/`,
        desc: 'For unrelated business income tax (UBIT)',
      },
      {
        name: 'Form CA 199',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}tax-exempt/efile-ftb-ca-form199-online/`,
        desc: 'California Exempt Organization Annual Information Return',
      },
    ],
    features: [
      { title: 'Form 3800', desc: 'General Business Credit' },
      { title: 'Form 3468', desc: 'Investment Credit' },
      {
        title: 'And More!',
        desc: 'Ensure complete and accurate filing with all necessary attachments',
      },
    ],
  },
  {
    displayOrder: 3,
    FormCategoryId: 2,
    title: 'Excise Forms',
    HighlightText1: 'Excise Tax',
    HighlightText2: 'Forms',
    description: '2290, 8849, 2290 Amendments',
    image: '/assets/images/exc-img.webp',
    icon: {
      webp: '/assets/images/excise-icon.webp',
      png: '/assets/images/excise-icon.png',
      alt: 'Excise Tax Forms',
    },
    bgColor: '#e7f8ed',
    heading: {
      text: 'Effortless E-Filing for IRS ',
      highlight: 'Excise Tax',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/`,
      suffix: ' Forms',
    },
    intro:
      "TaxZerone makes excise tax filing fast, accurate, and hassle-free. Whether you're filing for heavy highway vehicle use, claiming a refund, or making amendments, we provide a seamless e-filing experience.",
    whychoosetitle: 'Why TaxZerone Stands Above the Rest',
    whychoosedescription: '',
    supportedFormsTitle: 'We Support:',
    supportedForms: [
      {
        name: 'Heavy Vehicle Use Tax (HVUT)',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/`,
        desc: 'Form 2290',
      },
      {
        name: 'Claim for Refund of Excise Taxes',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/efile-irs-form-8849-schedule-6-online/`,
        desc: 'Form 8849 Schedule 6',
      },
      {
        name: '2290 Amendments',
        url: ``,
        desc: [
          '',
          {
            type: 'link',
            label: 'VIN Correction',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/efile-irs-form-2290-vin-correction-online/`,
          },
          ', ',
          {
            type: 'link',
            label: 'Weight Increase',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/efile-irs-form-2290-weight-increased-amendment/`,
          },
          ', ',
          {
            type: 'link',
            label: 'Mileage Exceeded',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}excise-tax/efile-irs-form-2290-mileage-exceeded-amendment/`,
          },
          '.',
        ],
      },
    ],
    features: [
      {
        title: 'Lightning-Fast Filing',
        desc: 'Get your IRS-stamped Schedule 1 in minutes.',
      },
      {
        title: 'Free VIN Corrections',
        desc: 'Made a mistake? Fix it at no extra cost.',
      },
      {
        title: 'Bulk Upload for Fleets',
        desc: 'File multiple vehicles in one go, hassle-free.',
      },
      {
        title: 'AutoMagic2290',
        desc: 'Save time by auto-filling details from your previous filing.',
      },
      {
        title: 'Real-Time IRS Updates',
        desc: 'Stay informed with instant status notifications.',
      },
      {
        title: 'Unmatched Customer Support',
        desc: 'Get expert help via 📱 phone (English & Spanish), 📧 email, and 💬 chat (Real People, No Chatbots)',
      },
    ],
  },
  {
    displayOrder: 4,
    FormCategoryId: 5,
    title: 'Information Returns',
    HighlightText1: 'Information',
    HighlightText2: 'Returns',
    description:
      'W-2, 1099, W-2G, 1097, 5498, 3921, 3922, 1098, and 1095 Forms',
    image: '/assets/images/inf-img.webp',
    icon: {
      webp: '/assets/images/information-icon.webp',
      png: '/assets/images/information-icon.png',
      alt: 'Information Returns',
    },
    bgColor: '#dae6f6',
    heading: {
      text: 'Comprehensive E-Filing for ',
      highlight: 'Information Returns',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}information-returns/`,
      suffix: '',
    },
    intro:
      'TaxZerone makes it <span style="font-weight: 600;">easy, accurate, and compliant </span> to file your information returns with the IRS and SSA. Whether you&apos;re reporting wages, payments, or health coverage, we support all major forms and their corrections.',
    whychoosetitle: '',
    whychoosedescription: '',
    supportedFormsTitle: 'We Support:',
    supportedForms: [
      {
        name: 'Wage & Tax Statement',
        url: ``,
        desc: [
          {
            type: 'link',
            label: 'W-2',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}information-returns/efile-irs-form-w2/`,
          },
          ', W-2C (Corrections)',
        ],
      },
      {
        name: '1099 Forms & Corrections',
        url: ``,
        desc: [
          {
            type: 'link',
            label: '1099-NEC',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}information-returns/efile-irs-form-1099-nec/`,
          },
          ', MISC, INT, DIV, K, R & more',
        ],
      },
      {
        name: 'Gambling & Stock Transactions',
        url: ``,
        desc: [
          {
            type: 'link',
            label: 'W-2G',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}information-returns/efile-irs-form-w-2g/`,
          },
          ', 3921, 3922',
        ],
      },
      {
        name: 'IRA & HSA Contributions',
        url: ``,
        desc: [
          {
            type: 'link',
            label: '5498',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}information-returns/efile-irs-form-5498/`,
          },
          ', 5498-ESA, 5498-SA',
        ],
      },
      {
        name: 'Affordable Care Act (ACA) Reporting',
        url: ``,
        desc: [
          {
            type: 'link',
            label: '1095-C',
            href: `${process.env.NEXT_PUBLIC_HOMESITE_URL}aca-forms/e-file-form-1095c/`,
          },
          ', 1095-B',
        ],
      },
    ],
    features: [],
  },
  {
    displayOrder: 5,
    FormCategoryId: 4,
    title: 'Employment Tax Forms',
    HighlightText1: 'Employment Tax',
    HighlightText2: 'Forms',
    description:
      '941, 941-X, 941 Schedule R, 940, 943, 943-X, 944, 945, 94X PIN Request',
    image: '/assets/images/emp-img.webp',
    icon: {
      webp: '/assets/images/employment-icon.webp',
      png: '/assets/images/employment-icon.png',
      alt: 'Employment Tax Forms',
    },
    bgColor: '#ddf3f6',
    heading: {
      text: 'Simplify Your ',
      highlight: 'Employment Tax',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/`,
      suffix: ' Filing with TaxZerone',
    },
    intro:
      "TaxZerone makes it easy to <span style='font-weight: 600;'>e-file your employment tax forms</span> quickly, securely, and affordably. Whether you're a small business, payroll provider, or tax professional, we've got you covered with full support for IRS employment tax forms.",
    whychoosetitle: '',
    whychoosedescription: '',
    supportedFormsTitle: 'We Support:',
    supportedForms: [
      {
        name: 'Form 941',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-941/`,
        desc: "Employer's Quarterly Federal Tax Return",
      },
      {
        name: 'Form 941-X',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/efile-irs-form-941-x-online/`,
        desc: "Adjusted Employer's Quarterly Federal Tax Return",
      },
      {
        name: 'Form 941 Schedule-R',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-941-schedule-r/`,
        desc: 'Allocation Schedule for Aggregate Filers',
      },
      {
        name: 'Form 940',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-940/`,
        desc: "Employer's Annual Federal Unemployment (FUTA) Tax Return",
      },
      {
        name: 'Form 943',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-943/`,
        desc: `Employer's Annual Federal Tax Return for Agricultural Employees`,
      },
      {
        name: 'Form 943-X',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-943-x/`,
        desc: `Adjusted Employer's Annual Federal Tax Return for Agricultural Employees`,
      },
      {
        name: 'Form 944',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-944/`,
        desc: "Employer's Annual Federal Tax Return (for small employers)",
      },
      {
        name: 'Form 945',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-945/`,
        desc: 'Annual Return of Withheld Federal Income Tax',
      },
      {
        name: 'Form 945-X',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-945-x/`,
        desc: 'Adjusted Annual Return of Withheld Federal Income Tax',
      },
      {
        name: '94X PIN Request',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}employment-tax/e-file-irs-form-94x-pin-request/`,
        desc: 'Securely request your IRS e-filing PIN for <strong>FREE</strong>',
      },
    ],
    features: [],
  },
  {
    displayOrder: 6,
    FormCategoryId: 8,
    title: 'Essential Forms',
    HighlightText1: 'Essential',
    HighlightText2: 'Forms',
    description: 'Form W-9, Form 8655, FinCEN BOI Report',
    image: '/assets/images/ess-img.webp',
    icon: {
      webp: '/assets/images/other-icon.webp',
      png: '/assets/images/other-icon.png',
      alt: 'Essential Forms',
    },
    bgColor: '#e5e4f7',
    heading: {
      text: 'IRS Forms for Taxpayer Identification & Authorization',
      highlight: '',
      link: `${process.env.NEXT_PUBLIC_HOMESITE_URL}request-form-w9-online-free/`,
      suffix: '',
    },
    intro:
      'TaxZerone makes it TaxZerone simplifies the process of handling essential IRS forms for businesses, individuals, and foreign entities. Our platform ensures accuracy, compliance, and ease of use.',
    whychoosetitle: '',
    whychoosedescription: '',
    supportedFormsTitle: '',
    supportedForms: [
      {
        name: 'Form W-9',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}request-form-w9-online-free/`,
        desc: 'Used by individuals and businesses to provide their Taxpayer Identification Number (TIN) to requestors for tax reporting purposes.',
      },
      {
        name: 'Form W-8 BEN',
        url: '', // No URL was provided in your markup
        desc: 'Required for non-U.S. individuals to certify their foreign status and claim tax treaty benefits to reduce withholding tax.',
      },
      {
        name: 'Form 8655',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}form-8655/`,
        desc: 'Authorizes a reporting agent to file payroll tax returns and make tax deposits on behalf of a business',
      },
      {
        name: 'BOIR',
        url: `${process.env.NEXT_PUBLIC_HOMESITE_URL}efile-boi-report/`,
        desc: 'Required for certain existing foreign companies to report information about their beneficial owners to FinCEN.',
      },
    ],
    features: [],
    nofeaturesdescription:
      'Manage these forms effortlessly with our intuitive and secure platform.',
  },
];
